import { useEffect, useRef, useState } from 'react';
import { entferneFoto, fotosZu } from '../fotos';
import { KameraIcon, MuellIcon, PfeilLinksIcon, PfeilRechtsIcon, PlusIcon } from '../icons';
import { erkenneZaehlerstand } from '../zaehlererkennung';
import { ZAEHLER_ART_LABEL, ZAEHLER_ARTEN, type Protokoll, type Zaehler as ZaehlerEintrag } from '../domain/types';
import { FotoGalerie } from './FotoGalerie';

interface ZaehlerProps {
	protokoll: Protokoll;
	onAendern: (protokoll: Protokoll) => void;
	onWeiter: () => void;
	onZurueck: () => void;
}

function dateiZuDataUrl(datei: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const leser = new FileReader();
		leser.onload = () => resolve(String(leser.result));
		leser.onerror = () => reject(leser.error);
		leser.readAsDataURL(datei);
	});
}

export function Zaehler({ protokoll, onAendern, onWeiter, onZurueck }: ZaehlerProps) {
	const [erkennungLaeuft, setErkennungLaeuft] = useState<Set<string>>(new Set());

	// Die Foto-Erkennung ist ein Netzwerk-Aufruf — bis die Antwort da ist,
	// kann `protokoll` sich längst geändert haben (neuer Zähler, andere
	// Fotos). Gleiches Muster wie in Rundgang.tsx: auf dem letzten
	// gerenderten Stand aufbauen, nicht auf dem von vor dem Aufruf.
	const protokollRef = useRef(protokoll);
	useEffect(() => {
		protokollRef.current = protokoll;
	}, [protokoll]);

	function hinzufuegen(art: ZaehlerEintrag['art']) {
		const neuer: ZaehlerEintrag = { id: crypto.randomUUID(), art, stand: '', fotoIds: [] };
		onAendern({ ...protokoll, zaehler: [...protokoll.zaehler, neuer] });
	}

	function aktualisieren(id: string, update: Partial<ZaehlerEintrag>) {
		onAendern({
			...protokoll,
			zaehler: protokoll.zaehler.map((z) => (z.id === id ? { ...z, ...update } : z))
		});
	}

	function entfernen(id: string) {
		const basis = protokollRef.current;
		const fotoIds = new Set(basis.zaehler.find((zaehler) => zaehler.id === id)?.fotoIds ?? []);
		const neu = {
			...basis,
			zaehler: basis.zaehler.filter((z) => z.id !== id),
			fotos: basis.fotos.filter((foto) => !fotoIds.has(foto.id))
		};
		protokollRef.current = neu;
		onAendern(neu);
	}

	async function fotoHinzufuegen(zaehlerId: string, datei: File | undefined) {
		if (!datei) return;
		const datenUrl = await dateiZuDataUrl(datei);
		const basis = protokollRef.current;
		const fotoId = crypto.randomUUID();
		const mitFoto = {
			...basis,
			fotos: [...basis.fotos, { id: fotoId, datenUrl, aufgenommenAm: new Date().toISOString() }],
			zaehler: basis.zaehler.map((z) => (z.id === zaehlerId ? { ...z, fotoIds: [...z.fotoIds, fotoId] } : z))
		};
		protokollRef.current = mitFoto;
		onAendern(mitFoto);

		setErkennungLaeuft((bisher) => new Set(bisher).add(zaehlerId));
		const erkennung = await erkenneZaehlerstand(datenUrl);
		setErkennungLaeuft((bisher) => {
			const neu = new Set(bisher);
			neu.delete(zaehlerId);
			return neu;
		});
		if (!erkennung) return;

		const aktuellerZaehler = protokollRef.current.zaehler.find((z) => z.id === zaehlerId);
		if (!aktuellerZaehler) return;
		const aktualisierung: Partial<ZaehlerEintrag> = {};
		if (erkennung.stand && !aktuellerZaehler.stand.trim()) aktualisierung.stand = erkennung.stand;
		if (erkennung.nummer && !aktuellerZaehler.nummer?.trim()) aktualisierung.nummer = erkennung.nummer;
		if (Object.keys(aktualisierung).length > 0) {
			onAendern({
				...protokollRef.current,
				zaehler: protokollRef.current.zaehler.map((z) => (z.id === zaehlerId ? { ...z, ...aktualisierung } : z))
			});
		}
	}

	function fotoLoeschen(fotoId: string) {
		const neu = entferneFoto(protokollRef.current, fotoId);
		protokollRef.current = neu;
		onAendern(neu);
	}

	return (
		<div className="schritt schritt--zaehler">
			<div className="schritt-kopf">
				<h2>Zählerstände</h2>
				<p className="schritt-erklaerung">
					Das Foto ist der eigentliche Beleg — den Stand liest die KI daraus meist automatisch mit, bitte trotzdem
					prüfen.
				</p>
			</div>

			<div className="chip-leiste">
				{ZAEHLER_ARTEN.map(({ art, label }) => (
					<button key={art} type="button" className="chip chip--hinzufuegen" onClick={() => hinzufuegen(art)}>
						<PlusIcon width={14} height={14} />
						{label}
					</button>
				))}
			</div>

			{protokoll.zaehler.length === 0 && (
				<p className="hinweis">Noch keine Zähler erfasst. Oben eine Art antippen, um einen hinzuzufügen.</p>
			)}

			<ul className="zaehler-liste">
				{protokoll.zaehler.map((z) => (
					<li key={z.id} className="zaehler-karte">
						<div className="zaehler-karte-kopf">
							<strong>{ZAEHLER_ART_LABEL[z.art]}</strong>
							<button type="button" className="icon-knopf" onClick={() => entfernen(z.id)} aria-label="Zähler entfernen">
								<MuellIcon width={16} height={16} />
							</button>
						</div>
						<div className="zaehler-felder">
							<input
								className="zaehler-feld"
								value={z.nummer ?? ''}
								onChange={(e) => aktualisieren(z.id, { nummer: e.target.value })}
								placeholder="Zählernummer (optional)"
							/>
							<input
								className="zaehler-feld zaehler-feld--stand"
								value={z.stand}
								onChange={(e) => aktualisieren(z.id, { stand: e.target.value })}
								placeholder="Zählerstand"
								inputMode="decimal"
							/>
						</div>
						<label className="foto-knopf foto-knopf--breit">
							<KameraIcon width={16} height={16} />
							{z.fotoIds.length > 0 ? 'Weiteres Zählerfoto' : 'Zählerfoto aufnehmen'}
							<input
								type="file"
								accept="image/*"
								capture="environment"
								onChange={(e) => {
									void fotoHinzufuegen(z.id, e.target.files?.[0]);
									e.target.value = '';
								}}
							/>
						</label>
						{erkennungLaeuft.has(z.id) && <p className="verarbeitung-hinweis">Lese Zählerstand …</p>}
						<FotoGalerie
							fotos={fotosZu(protokoll, z.fotoIds)}
							label={`Zählerfotos ${ZAEHLER_ART_LABEL[z.art]}`}
							onEntfernen={fotoLoeschen}
						/>
					</li>
				))}
			</ul>

			<footer className="schritt-fuss">
				<button type="button" className="nebenknopf" onClick={onZurueck}>
					<PfeilLinksIcon width={18} height={18} />
					Zurück
				</button>
				<button type="button" className="hauptknopf hauptknopf--mit-icon" onClick={onWeiter}>
					Weiter
					<PfeilRechtsIcon width={20} height={20} />
				</button>
			</footer>
		</div>
	);
}
